<script setup>
import {computed, inject, reactive, watch} from "vue";
//import functions
//import components
import {FloatLabel, Dialog, MultiSelect, DatePicker, Select} from "primevue";
import {Icon} from "@iconify/vue";
//import composables
import {useBreakpoint} from "@/composables/useBreakpoint.js";
import {useLoadingFlags} from "@/composables/useLoadingFlags.js";
import {useConfigData} from "@/composables/useConfigData.js";

//loading flags
const {
  saving,
} = useLoadingFlags()

//composables
const {isXL} = useBreakpoint();
const {games} = useConfigData('Currency')

//emits
const emit = defineEmits(['applyFilter']);

//props
const props = defineProps({
  accounts: {
    type: Array,
    default: () => [],
  },
  servers: {
    type: Array,
    default: () => [],
  },
  filters: {
    type: Object,
    default: () => ({}),
  },
});

//injects
const visible = inject('isShowFiltersModal')

//component variables
const sides = ['Withdraw', 'Deposit']
const references = ['True', 'False']

const createDefaultFilters = () => ({
  servers: [],
  accounts: [],
  hasReference: null,
  games: [],
  side: null,
  createdFrom: null,
  createdTo: null,
})

const normalizeFilters = (source = {}) => ({
  ...createDefaultFilters(),
  ...source,
  servers: Array.isArray(source.servers) ? [...source.servers] : [],
  accounts: Array.isArray(source.accounts) ? [...source.accounts] : [],
  games: Array.isArray(source.games) ? [...source.games] : [],
})

const filters = reactive(createDefaultFilters())

const assignFilters = (source = {}) => {
  Object.assign(filters, normalizeFilters(source))
}

const createdFromModel = computed({
  get() {
    if (filters.createdFrom && !isNaN(filters.createdFrom)) {
      return new Date(Number(filters.createdFrom));
    }
    return null;
  },
  set(value) {
    filters.createdFrom = value instanceof Date ? value.getTime() : null;
  }
})

const createdToModel = computed({
  get() {
    if (filters.createdTo && !isNaN(filters.createdTo)) {
      return new Date(Number(filters.createdTo));
    }
    return null;
  },
  set(value) {
    filters.createdTo = value instanceof Date ? value.getTime() : null;
  }
})

//global functions

//component functions
const applyFilter = () => {
  emit("applyFilter", normalizeFilters(filters))
  visible.value = false
}

const resetFilters = () => {
  assignFilters()
}

function selectNow_createdFrom() {
  const now = new Date();
  filters.createdFrom = now.getTime();
}

function clear_createdFrom() {
  filters.createdFrom = null;
}
function selectNow_createdTo() {
  const now = new Date();
  filters.createdTo = now.getTime();
}

function clear_createdTo() {
  filters.createdTo = null;
}

//hooks
watch(
    () => props.filters,
    (value) => assignFilters(value),
    {immediate: true, deep: true}
)

//provides
</script>
<template>
  <Dialog v-model:visible="visible"
          :class="['max-w-3xl !border-0 !rounded-md !bg-[#0e1726]', isXL ? '' : 'w-full']"
          :position="isXL ? 'center' : 'bottom'"
          closable dismissable-mask draggable modal
          content-class="flex flex-col gap-4 overflow-hidden h-full w-full max-w-[calc(100vw-20px)] max-h-[calc(100vh-100px)]">
    <template #header>
      <div class="flex flex-row justify-between w-full">
        <div class="text-base py-3">
          Filters
        </div>
        <button @click="resetFilters" class="hover:underline text-xs">Reset Filters</button>
      </div>
    </template>
    <template #closebutton="{closeCallback}">
      <div class="h-full w-[1px] bg-slate-700"></div>
      <button
          type="button"
          class="text-white-dark outline-none hover:text-red-500"
          @click="closeCallback"
      >
        <Icon icon="pajamas:close-xs"/>
      </button>
    </template>
    <div class="flex-1 flex flex-col gap-4 overflow-y-auto pt-4 pb-1 text-xs 2xl:text-sm min-w-80 max-w-full">
      <div class="grid grid-cols-12 gap-4">
        <FloatLabel class="col-span-4 max-w-[320px] custom-multiselect" variant="on">
          <MultiSelect v-model="filters.games" inputId="game" display="chip"
                       :options="games"
                       optionLabel="game"
                       optionValue="game"
                       filter auto-filter-focus show-clear
                       :maxSelectedLabels="0"
                       class="w-full min-w-28 !bg-transparent h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700"
                       input-class="!text-xs 2xl:!text-sm"
                       overlayClass="custom-select-overlay"/>
          <label for="game" class="!bg-slate-900">Game</label>
        </FloatLabel>
        <FloatLabel class="col-span-4 max-w-[320px] custom-multiselect" variant="on">
          <MultiSelect v-model="filters.servers" inputId="server" display="chip"
                       :options="servers"
                       optionLabel="label"
                       optionValue="value"
                       filter auto-filter-focus show-clear
                       :maxSelectedLabels="0"
                       class="w-full min-w-28 !bg-transparent h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700"
                       input-class="!text-xs 2xl:!text-sm"
                       overlayClass="custom-select-overlay"/>
          <label for="server" class="!bg-slate-900">Server</label>
        </FloatLabel>
        <FloatLabel class="col-span-4 max-w-[320px] custom-multiselect" variant="on">
          <MultiSelect v-model="filters.accounts" inputId="account" display="chip"
                       :options="accounts"
                       optionLabel="label"
                       optionValue="value"
                       filter auto-filter-focus show-clear
                       :maxSelectedLabels="0"
                       class="w-full min-w-28 !bg-transparent h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700"
                       input-class="!text-xs 2xl:!text-sm"
                       overlayClass="custom-select-overlay"/>
          <label for="account" class="!bg-slate-900">Account</label>
        </FloatLabel>
        <FloatLabel  class="col-span-6 max-w-[320px] custom-multiselect" variant="on">
          <Select v-model="filters.side" input-id="side" :options="sides"
                  class="w-full min-w-28 !bg-transparent h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700"
                  label-class="!text-sm !items-center !flex">
          </Select>
          <label for="side" class="!bg-slate-900">Side</label>
        </FloatLabel>
        <FloatLabel  class="col-span-6 max-w-[320px] custom-multiselect" variant="on">
          <Select v-model="filters.hasReference" input-id="reference" :options="references"
                  class="w-full min-w-28 !bg-transparent h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700"
                  label-class="!text-sm !items-center !flex">
          </Select>
          <label for="reference" class="!bg-slate-900">Has Reference</label>
        </FloatLabel>
        <div class="col-span-6 flex flex-col gap-0.5 text-xs 2xl:text-sm">
          <FloatLabel variant="on">
            <DatePicker
                inputId="createdFrom"
                v-model="createdFromModel"
                hourFormat="24"
                dateFormat="d M yy"
                fluid
                :showButtonBar="true"
                class="flex-1"
                size="small"
                :input-class="['!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700']"
                showIcon
                iconDisplay="input"
            >
              <template #todaybutton>
                <span @click="selectNow_createdFrom"
                      class="cursor-pointer px-2 py-1 rounded-md text-xs hover:bg-gray-700/50">Now</span>
              </template>
              <template #clearbutton>
                <span @click="clear_createdFrom"
                      class="cursor-pointer px-2 py-1 rounded-md text-xs hover:bg-gray-700/50">Clear</span>
              </template>
            </DatePicker>
            <label for="createdFrom" class="!bg-slate-900">Create From</label>
          </FloatLabel>
        </div>
        <div class="col-span-6 flex flex-col gap-0.5 text-xs 2xl:text-sm">
          <FloatLabel variant="on">
            <DatePicker
                inputId="createdTo"
                v-model="createdToModel"
                hourFormat="24"
                dateFormat="d M yy"
                fluid
                :showButtonBar="true"
                class="flex-1"
                size="small"
                :input-class="['!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700']"
                showIcon
                iconDisplay="input"
            >
              <template #todaybutton>
                <span @click="selectNow_createdTo"
                      class="cursor-pointer px-2 py-1 rounded-md text-xs hover:bg-gray-700/50">Now</span>
              </template>
              <template #clearbutton>
                <span @click="clear_createdTo"
                      class="cursor-pointer px-2 py-1 rounded-md text-xs hover:bg-gray-700/50">Clear</span>
              </template>
            </DatePicker>
            <label for="createdTo" class="!bg-slate-900">Create To</label>
          </FloatLabel>
        </div>
      </div>
    </div>
    <div class="flex justify-end gap-3 select-none">
      <button @click="applyFilter"
              class="btn btn-outline-dark flex items-center justify-center gap-3 disabled:opacity-50 py-1">
        <Icon v-if="saving" icon="eos-icons:bubble-loading" height="24"/>
        Apply
      </button>
    </div>
  </Dialog>
</template>
<style></style>
